<script>
  import { Button, Pagination } from "paxray-components-svelte"
  import CardColumn from "./CardColumn.svelte";
  import SeamlessGridHeader from "./common/SeamlessGridHeader.svelte";
  import SearchNew from "./searchNew.svelte";
  import { INFO_ICON } from '@utils/icons.js';

  export let projects = [];
  export let headerName = "Projects in Progress";
  export const description = "";
  export let tooltipText = "";
  export let accentColor = "";
  export let handleOpenSidePanel = null;
  export let handleOpenProject = null;
  export let handleCreateProject = null;
  export let showCreateProject = false;
  export let currentProject = null;

  let searchTerm = "";
  let currentPage = 1;
  let innerHeight = 0;

  // Feste Maße in rem aus dem Layout, damit sich die Kartenanzahl nicht über die eigene Höhe rückkoppelt.
  const rootFontSize =
    parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const CARD_HEIGHT = 6.075;
  const ITEM_GAP = 0.6; // --control-gap
  const HEADERS_HEIGHT = 8; // App-Header und Spaltenheader
  const COLUMN_PADDING = 2;
  const SEARCH_RESERVE = 2.5 + ITEM_GAP;
  const PAGINATION_RESERVE = 4; // Footer in --app-header-height

  const cardsFitting = (height) =>
    Math.floor((height + ITEM_GAP) / (CARD_HEIGHT + ITEM_GAP));

  $: availableHeight =
    innerHeight / rootFontSize -
    HEADERS_HEIGHT -
    COLUMN_PADDING -
    (showCreateProject ? CARD_HEIGHT + ITEM_GAP : 0);
  $: itemsPerPage = Math.max(
    1,
    projects.length > cardsFitting(availableHeight)
      ? cardsFitting(availableHeight - SEARCH_RESERVE - PAGINATION_RESERVE)
      : cardsFitting(availableHeight),
  );

  const formatStatus = (status = "") =>
    status && status === status.toUpperCase()
      ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
      : status;

  const formatCurrentPhase = (phase, fallbackStatus = "") =>
    ({
      SETUP: "Setup",
      SCOPING: "Scoping",
      TASK_REGISTRATION: "Discovery",
      DATA_COLLECTION: "Discovery Extended",
      ANALYSIS: "Analysis",
    }[phase] || fallbackStatus);

  const projectDisplayName = (project) =>
    project?.isPotentialScout &&
    String(project?.name || "").startsWith("Operational Excellence Pulse 2026")
      ? "Demo Potential Scout"
      : project?.name === "Demo"
      ? project.isPotentialScout
        ? "Demo Potential Scout"
        : "Demo Task Mining"
      : project?.name;

  const matchesSearch = (project, value) => {
    const query = value.trim().toLowerCase();
    if (!query) return true;
    return String(projectDisplayName(project) || "")
      .toLowerCase()
      .includes(query);
  };

  const createdTime = (project) => {
    const time = Date.parse(project?.created_when || "");
    return Number.isNaN(time) ? 0 : time;
  };

  // Neueste Projekte zuerst, ohne Datum alphabetisch.
  const byNewest = (a, b) =>
    createdTime(b) - createdTime(a) ||
    String(projectDisplayName(a) || "").localeCompare(
      String(projectDisplayName(b) || ""),
    );

  $: filteredProjects = projects
    .filter((project) => matchesSearch(project, searchTerm))
    .sort(byNewest);
  $: totalPages = Math.max(1, Math.ceil(filteredProjects.length / itemsPerPage));
  $: if (currentPage > totalPages) currentPage = totalPages;
  $: paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  $: if (searchTerm) currentPage = 1;
</script>

<svelte:window bind:innerHeight />

<div class="project_column" data-project-column={headerName}>
  <SeamlessGridHeader
    header={headerName}
    showLeftIndicator={Boolean(accentColor)}
    indicatorColor={accentColor}
    tooltipText={tooltipText}
    rightIndicatorValue={filteredProjects.length}
  />
  <div class="project_column_items">
  {#if projects.length > itemsPerPage}
    <div class="project_column_search">
      <SearchNew bind:searchTerm placeholder="Projects" />
    </div>
  {/if}
  {#if showCreateProject}
    <div class="project_card_item create_project_item" style={`--accent-color: ${accentColor};`}>
      <CardColumn onClick={handleCreateProject} minHeight="6.075rem">
        <header class="card_header create_project_card">
          <div class="card_title_row">
            <div class="card_title_group">
              <h3 class="card_title">New Project</h3>
              <span class="card_status">Create and configure a project</span>
            </div>
            <span class="create_icon" aria-hidden="true">+</span>
          </div>
          <div class="type">
            <p>
              <span class="type_label">Type:</span>
              <span class="type_value">Task Mining or Potential Scout</span>
            </p>
          </div>
        </header>
      </CardColumn>
    </div>
  {/if}
  {#each paginatedProjects as project}
    <div
      class="project_card_item"
      class:disabled_project_card={project.disabled}
      aria-disabled={project.disabled}
      style={`--accent-color: ${accentColor};`}
    >
      <CardColumn
        onClick={project.disabled ? null : () => handleOpenProject?.(project)}
        selected={currentProject?.id === project.id}
        minHeight="6.075rem"
        clickable={!project.disabled}
      >
        <header class="card_header">
          <div class="card_title_row">
            <div class="card_title_group">
              <h3 class="card_title" title={projectDisplayName(project)}>
                {projectDisplayName(project)}
              </h3>
            </div>
          </div>

          <div class="project_tags">
            <span class="project_type_pill">{project.type || "Task Analysis"}</span>
            {#if project.archived || formatCurrentPhase(project.current_stage, formatStatus(project.status))}
              <span class="project_type_pill project_phase_pill">
                {project.archived
                  ? "Archived"
                  : formatCurrentPhase(project.current_stage, formatStatus(project.status))}
              </span>
            {/if}
            <span class="card_info_button">
              <Button
                icon={INFO_ICON}
                size="small"
                kind="secondary"
                iconDescription="Project details"
                on:click={(event) => {
                  event.stopPropagation();
                  handleOpenSidePanel?.(project);
                }}
              />
            </span>
          </div>
        </header>
      </CardColumn>
    </div>
  {/each}
  {#if filteredProjects.length === 0 && !showCreateProject}
    <div class="empty_state">No projects in this area</div>
  {/if}
  </div>
  {#if filteredProjects.length > itemsPerPage}
    <div class="project_pagination">
      <Pagination
        small
        basic
        bind:page={currentPage}
        totalItems={filteredProjects.length}
        pageSize={itemsPerPage}
      />
    </div>
  {/if}
</div>

<style>
  .project_column {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-width: 0;
    height: 100%;
    overflow: hidden;
    border-left: 1px solid var(--border-color);
  }

  .project_column_items {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: var(--control-gap);
    min-height: 0;
    padding: 1rem;
    overflow-y: auto;
  }

  .project_column_search {
    flex-shrink: 0;
    padding: 0;
  }

  .project_column_search :global(.search) {
    height: 2.5rem;
    border-color: var(--border-color, #d9d9d9);
  }

  .project_column_search :global(.searchicon) {
    height: 1rem;
  }

  .project_column_search :global(input) {
    height: 2.25rem;
    font-size: 0.8125rem;
  }

  .project_pagination {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    box-sizing: border-box;
    height: var(--app-header-height);
    padding: 0 1rem;
    border-top: 1px solid var(--border-color);
  }

  .project_pagination > :global(*) {
    flex: 1;
  }

  .project_card_item {
    --project-card-radius: 0.5rem;
    --project-pill-radius: 0.25rem;
    width: 100%;
  }

  .project_card_item :global(.info_card) {
    padding: var(--control-gap);
    gap: var(--control-gap);
    border: 1px solid var(--border-color, #d9d9d9);
    border-left: 1px solid var(--border-color, #d9d9d9);
    border-radius: var(--project-card-radius);
    transition: border-color 0.15s ease;
  }

  .project_card_item :global(.info_card:hover),
  .project_card_item :global(.info_card:focus-within) {
    border-color: var(--pax-black, #1c1c20);
  }

  .project_card_item :global(.info_card.selected) {
    border: 1px solid var(--pax-black, #1c1c20);
  }

  .disabled_project_card :global(.info_card) {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .create_project_item :global(.info_card) {
    border-style: dashed;
    background: #fafafa;
  }

  .create_project_item :global(.info_card:hover),
  .create_project_item :global(.info_card:focus-within) {
    background: #f4f4f4;
  }

  .create_icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.875rem;
    height: 1.875rem;
    flex-shrink: 0;
    border-radius: 0.5rem;
    color: white;
    background: var(--pax-black, #1c1c20);
    font-size: 1.25rem;
    line-height: 1;
  }

  .card_header {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: space-between;
    gap: var(--m);
  }

  .project_tags {
    display: flex;
    align-items: flex-end;
    flex-wrap: nowrap;
    gap: var(--control-gap);
    min-width: 0;
  }

  .project_type_pill {
    flex-shrink: 0;
    white-space: nowrap;
    padding: 0.3rem 0.4rem;
    border: 1px solid var(--border-color, #d9d9d9);
    border-radius: var(--project-pill-radius);
    background: transparent;
    color: var(--pax-black, #1c1c20);
    font-size: 0.6875rem;
    font-weight: 400;
    line-height: 1;
  }

  .card_info_button {
    display: flex;
    flex-shrink: 0;
    margin-left: auto;
  }

  .card_info_button :global(.bx--btn) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    min-height: 0;
    padding: 0;
    border-radius: var(--project-pill-radius);
  }

  .card_info_button :global(.bx--btn svg) {
    width: 1.125rem;
    height: 1.125rem;
  }

  .project_phase_pill {
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    background: transparent;
  }

  .card_title_row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .card_title_group {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .card_title {
    min-width: 0;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--pax-black, #1c1c20);
    font-family: Inter;
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .empty_state {
    padding: 1.25rem;
    border: 1px dashed var(--border-color, #d9d9d9);
    border-radius: 0.5rem;
    color: var(--pax-sub, #474752);
    font-size: 0.75rem;
    text-align: center;
  }

  .card_status {
    color: var(--pax-black, #1c1c20);
    font-family: Inter;
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1.2;
  }

  .type {
    margin-top: 0.15rem;
  }

  .type p {
    margin: 0;
  }

  .type_label {
    color: var(--pax-sub, #474752);
    font-family: Inter;
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
  }

  .type_value {
    color: var(--pax-sub, #474752);
    font-family: Inter;
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 700;
    line-height: 120%;
  }
</style>
